import posthog from "posthog-js";
import { useAuth as useOidcAuth } from "react-oidc-context";
import { PostHogProvider, usePostHog } from "posthog-js/react";
import { Box } from "@mui/material";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type PropsWithChildren,
} from "react";
import { runtimeConfig } from "@/shared/config/runtimeConfig";
import AppErrorBoundary from "@/shared/ui/AppErrorBoundary";
import { SYSTEM_BANNER_FONT_SIZE_PX } from "@/shared/ui/systemBannerStyles";

export const PostHogResetContext = createContext<() => void>(() => undefined);

function readProfileString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

export function PostHogIdentityBridge({ children }: Readonly<PropsWithChildren>) {
  const { user, isAuthenticated } = useOidcAuth();
  const posthog = usePostHog();
  const identifiedDistinctIdRef = useRef<string | null>(null);

  const reset = useCallback(() => {
    posthog.reset();
    identifiedDistinctIdRef.current = null;
  }, [posthog]);

  useEffect(() => {
    const profile = user?.profile;
    const distinctId = isAuthenticated ? readProfileString(profile?.sub) : undefined;

    if (!distinctId) {
      if (identifiedDistinctIdRef.current) {
        reset();
      }
      return;
    }

    if (identifiedDistinctIdRef.current === distinctId) {
      return;
    }

    // A new OIDC subject in the same browser must not inherit the prior account's
    // anonymous or identified history.
    if (identifiedDistinctIdRef.current) {
      reset();
    }

    const email = readProfileString(profile?.email);
    const name = readProfileString(profile?.name);
    posthog.identify(distinctId, {
      ...(email ? { email } : {}),
      ...(name ? { name } : {}),
    });
    identifiedDistinctIdRef.current = distinctId;
  }, [isAuthenticated, posthog, reset, user]);

  return <PostHogResetContext.Provider value={reset}>{children}</PostHogResetContext.Provider>;
}

/** Clears the identified browser session before the app redirects to the IdP. */
export function usePostHogReset(): () => void {
  return useContext(PostHogResetContext);
}

/** Full-viewport fallback. Type matches the demo-mode system banner. */
export function PostHogErrorFallback() {
  return (
    <Box
      component="p"
      role="alert"
      sx={{
        boxSizing: "border-box",
        m: 0,
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        fontFamily: "var(--font-sans)",
        fontSize: `${SYSTEM_BANNER_FONT_SIZE_PX}px`,
      }}
    >
      Something went wrong. Please refresh and try again.
    </Box>
  );
}

export default function PostHogRoot({ children }: Readonly<PropsWithChildren>) {
  const apiKey = runtimeConfig.POSTHOG_PROJECT_TOKEN.trim();
  const apiHost = runtimeConfig.POSTHOG_HOST.trim();

  if (!apiKey || !apiHost) {
    if (import.meta.env.DEV) {
      const missingVariable = !apiKey
        ? "VITE_PUBLIC_POSTHOG_PROJECT_TOKEN"
        : "VITE_PUBLIC_POSTHOG_HOST";
      throw new Error(
        `${missingVariable} variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once ${missingVariable} is configured`,
      );
    }

    return children;
  }

  return (
    // initPostHog (main.tsx) has already started this client, before the first render.
    <PostHogProvider client={posthog}>
      <PostHogIdentityBridge>
        <AppErrorBoundary fallback={<PostHogErrorFallback />}>{children}</AppErrorBoundary>
      </PostHogIdentityBridge>
    </PostHogProvider>
  );
}
